import type {
  PmoFlowSummaryParams,
  PmoFlowSummaryResponse,
  PmoFlowTrendParams,
  PmoFlowTrendResponse,
  PmoScaleExcludeMode,
  PmoScaleLeadTimeResponse,
  PmoScaleScopeKey,
  PmoScaleTailPercentile,
  PmoScaleTailProjectsResponse,
  PmoSupplyRateProjectsResponse,
  PmoSupplyRateResponse,
  PmoSupplyRateStage,
} from '../../types/pmo-flow.js';
import { request } from './_request.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムAPIクライアント。getSummary/getTrendでフェーズ区間別所要日数のサマリーと月次コホートトレンドを、getSupplyRateでアサイン可能な案件・バグの充足率を、getScaleLeadTimeで案件規模別リードタイムを取得
 * @graph-connects /kpi/pmo-flow/summary [calls] フェーズ区間別リードタイムサマリーAPI
 * @graph-connects /kpi/pmo-flow/trend [calls] フェーズ区間別月次コホートトレンドAPI
 * @graph-connects /kpi/pmo-flow/supply-rate [calls] アサイン可能な案件・バグの充足率API
 * @graph-connects /kpi/pmo-flow/supply-rate/projects [calls] アサイン可能な案件・バグの明細一覧API
 * @graph-connects /kpi/pmo-flow/scale-lead-time [calls] 案件規模別リードタイムAPI
 * @graph-connects /kpi/pmo-flow/scale-lead-time/projects [calls] 規模帯の指定分位（p75/p90）以上の案件明細API
 */
export const pmoFlowApi = {
  getSummary: async (params?: PmoFlowSummaryParams): Promise<PmoFlowSummaryResponse> => {
    const query = new URLSearchParams();
    if (params?.months !== undefined) query.set('months', String(params.months));
    const queryString = query.toString();
    return await request<PmoFlowSummaryResponse>(
      `/kpi/pmo-flow/summary${queryString ? `?${queryString}` : ''}`,
    );
  },
  getTrend: async (params: PmoFlowTrendParams): Promise<PmoFlowTrendResponse> => {
    // segment は必須なのでクエリは常に非空になる
    const query = new URLSearchParams();
    query.set('segment', params.segment);
    if (params.months !== undefined) query.set('months', String(params.months));
    return await request<PmoFlowTrendResponse>(`/kpi/pmo-flow/trend?${query.toString()}`);
  },
  getSupplyRate: async (): Promise<PmoSupplyRateResponse> => {
    // 全社横断・期間セレクト非連動（分母は先2週間分の予算）のためクエリパラメータは持たない
    return await request<PmoSupplyRateResponse>('/kpi/pmo-flow/supply-rate');
  },
  getSupplyRateProjects: async (
    stage: PmoSupplyRateStage,
  ): Promise<PmoSupplyRateProjectsResponse> => {
    // 抽出条件はサーバー側でカードの集計と共有しているため、段の指定だけを渡す
    return await request<PmoSupplyRateProjectsResponse>(
      `/kpi/pmo-flow/supply-rate/projects?stage=${stage}`,
    );
  },
  getScaleLeadTime: async (
    month: string | null,
    exclude: PmoScaleExcludeMode,
  ): Promise<PmoScaleLeadTimeResponse> => {
    // 月未指定はサーバー側の既定（最新月）に委ねる。初回描画のためだけに
    // クライアントで「今月」を計算すると、月替わりの日にサーバーとずれた月を要求する
    const query = new URLSearchParams({ exclude });
    if (month !== null) {
      query.set('month', month);
    }
    return await request<PmoScaleLeadTimeResponse>(
      `/kpi/pmo-flow/scale-lead-time?${query.toString()}`,
    );
  },
  getScaleTailProjects: async (
    scope: PmoScaleScopeKey,
    month: string,
    percentile: PmoScaleTailPercentile,
    exclude: PmoScaleExcludeMode,
  ): Promise<PmoScaleTailProjectsResponse> => {
    // しきい値の営業日はサーバー側で母集団から計算するので、対象・分位・月・除外だけを渡す
    const query = new URLSearchParams({
      scope,
      month,
      percentile: String(percentile),
      exclude,
    });
    return await request<PmoScaleTailProjectsResponse>(
      `/kpi/pmo-flow/scale-lead-time/projects?${query.toString()}`,
    );
  },
};
